import type { EstimateConfig, Pricing } from "./schema.ts"

/**
 * The cost estimator: pure functions shared by the page build (each app's "cost to own" at its default
 * usage) and the browser (the sliders on every app page). Prices come only from pricing.json, which
 * records Cloudflare's published rates with their sources and the date they were read.
 */

export type SliderValues = Readonly<Record<string, number>>

/** Monthly usage for every "primitive.meter": the sum of coefficient × term over the meter's terms. */
export const usage = (config: EstimateConfig, values: SliderValues): Record<string, number> => {
  const out: Record<string, number> = {}
  for (const [key, terms] of Object.entries(config.meters)) {
    let total = 0
    for (const [term, coefficient] of Object.entries(terms)) {
      // A term is a slider id, a product of slider ids ("posts*channels"), or "one" (a fixed baseline).
      total +=
        coefficient *
        term.split("*").reduce((product, id) => product * (id === "one" ? 1 : Number(values[id]) || 0), 1)
    }
    out[key] = total
  }
  return out
}

export interface MeterLine {
  readonly id: string
  readonly unit: string
  readonly usage: number
  readonly included: number | null
  readonly freeMonthly: number | null
  readonly pricePer: number
  readonly per: number
  readonly billable: number
  readonly cost: number
}

export interface PrimitiveLine {
  readonly id: string
  readonly name: string
  readonly source: string
  readonly meters: ReadonlyArray<MeterLine>
  readonly cost: number
}

export interface Estimate {
  readonly usage: Record<string, number>
  readonly rows: ReadonlyArray<PrimitiveLine>
  readonly needsPaid: boolean
  readonly reasons: ReadonlyArray<string>
  readonly base: number
  readonly total: number
}

/**
 * Workers Paid is needed when the app requires it, when a paid-only primitive is used, or when usage
 * exceeds a free-plan allowance. On Paid the bill is the $5 base plus anything beyond what's included.
 */
export const estimate = (
  pricing: Pricing,
  config: EstimateConfig,
  values: SliderValues,
  { paidRequired = false }: { readonly paidRequired?: boolean } = {},
): Estimate => {
  const used = usage(config, values)
  const reasons: string[] = []
  let needsPaid = paidRequired
  if (paidRequired) reasons.push("this app needs Workers Paid features")

  const drafts = new Map<
    string,
    { id: string; name: string; source: string; meters: Omit<MeterLine, "billable" | "cost">[] }
  >()
  for (const [key, amount] of Object.entries(used)) {
    const [primitiveId = "", meterId = ""] = key.split(".")
    const primitive = pricing.primitives[primitiveId]
    const meter = primitive?.meters[meterId]
    if (!primitive || !meter) throw new Error(`unknown meter ${key}`)
    if (amount > 0 && primitive.paidOnly && !needsPaid) {
      needsPaid = true
      reasons.push(`${primitive.name} is on Workers Paid only`)
    }
    if (meter.freeMonthly !== null && amount > meter.freeMonthly) {
      if (!primitive.paidOnly) reasons.push(`${primitive.name} ${meter.unit} exceed the free allowance`)
      needsPaid = true
    }
    const draft = drafts.get(primitiveId) ?? {
      id: primitiveId,
      name: primitive.name,
      source: primitive.source,
      meters: [],
    }
    draft.meters.push({
      id: meterId,
      unit: meter.unit,
      usage: amount,
      included: meter.paidIncluded,
      freeMonthly: meter.freeMonthly,
      pricePer: meter.pricePer,
      per: meter.per,
    })
    drafts.set(primitiveId, draft)
  }

  const base = needsPaid ? pricing.plans.paidBase : 0
  const rows: PrimitiveLine[] = [...drafts.values()].map((draft) => {
    const meters = draft.meters.map((m): MeterLine => {
      // On the free plan nothing is billed (we've already decided it fits); on Paid, only the overage.
      const billable = needsPaid && m.included !== null ? Math.max(0, m.usage - m.included) : 0
      return { ...m, billable, cost: (billable * m.pricePer) / m.per }
    })
    return { ...draft, meters, cost: meters.reduce((sum, m) => sum + m.cost, 0) }
  })

  return {
    usage: used,
    rows,
    needsPaid,
    reasons: [...new Set(reasons)],
    base,
    total: rows.reduce((sum, row) => sum + row.cost, base),
  }
}

export const money = (value: number): string =>
  value < 0.005
    ? "$0.00"
    : value < 100
      ? `$${value.toFixed(2)}`
      : `$${Math.round(value).toLocaleString("en-US")}`

export const quantity = (value: number): string =>
  value >= 1e9
    ? `${(value / 1e9).toFixed(value >= 1e10 ? 0 : 1)}B`
    : value >= 1e6
      ? `${(value / 1e6).toFixed(value >= 1e7 ? 0 : 1)}M`
      : value >= 1e3
        ? `${(value / 1e3).toFixed(value >= 1e4 ? 0 : 1)}k`
        : (Math.round(value * 100) / 100).toString()
